/**
 * One counted figure on a dashboard (ui-audit data-and-charts §3): a quiet
 * label, the value as the largest text on the tile (`figure`, tabular), then
 * one line of context — the period, the base of a percentage, a status. No
 * icon plate: a tile says what it counts in words. A value the screen could
 * not obtain is `null` and renders as "—", never as a zero.
 */

import React from 'react';
import { Card, Skeleton, Typography, useTheme } from '@mui/material';

export interface KpiTileProps {
  /** What is counted, in the user's words. */
  label: string;
  /** The figure, already formatted. `null` = not available (renders "—"). */
  value: React.ReactNode | null;
  /** One line under the value: period, base, comparison or a `StatusDot`. */
  detail?: React.ReactNode;
  loading?: boolean;
  /** The value is a name, not a count (the busiest account): set a step smaller than a figure
   *  and cut at two lines, so a long name cannot outgrow the row of tiles. */
  textValue?: boolean;
}

export const KpiTile: React.FC<KpiTileProps> = ({ label, value, detail, loading = false, textValue = false }) => {
  const theme = useTheme();

  return (
    <Card sx={{ p: 2, height: '100%', minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {loading ? (
        <Skeleton width="60%" sx={{ typography: 'figure' }} />
      ) : (
        <Typography
          variant={textValue ? 'h3' : 'figure'}
          component="div"
          title={textValue && typeof value === 'string' ? value : undefined}
          sx={{
            mt: 0.5,
            overflowWrap: 'anywhere',
            ...(textValue && { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}
        >
          {value ?? '—'}
        </Typography>
      )}
      {detail && !loading && (
        <Typography variant="body2" component="div" sx={{ mt: 0.5, color: theme.custom.text.tertiary }}>
          {detail}
        </Typography>
      )}
    </Card>
  );
};

export default KpiTile;
